<template>
	<div class="place-contact-popup-content">
		<h3 id="place-popup-title">
			{{ t('maps', 'New contact address') }}
		</h3>
		<span v-if="addressLoading"
			class="icon icon-loading-small" />
		<textarea v-else
			id="placeContactPopupAddress"
			v-model="formattedAddress"
			@input="addressEdited = true" />
		<br>
		<div class="contact-select">
			<label for="userMultiselect">
				<span class="icon icon-user" />
			</label>
			<NcSelect
				id="userMultiselect"
				ref="userMultiselect"
				v-model="selectedContact"
				class="contact-input"
				label="FN"
				:get-option-key="getContactKey"
				:aria-label-combobox="t('maps', 'Choose a contact')"
				:placeholder="t('maps', 'Choose a contact')"
				:loading="contactsLoading"
				:options="contactData"
				@open="loadContacts">
				<template #option="option">
					<Avatar
						class="contact-avatar"
						:is-no-user="true"
						:url="option.AVATAR_URL"
						:user="option.FN" />
					{{ option.FN }}
				</template>
			</NcSelect>
		</div>
		<div class="address-type"
			:name="t('maps', 'Address type')">
			<label for="addressTypeSelect">
				<span class="icon icon-address" />
			</label>
			<select id="addressTypeSelect"
				v-model="addressType"
				:disabled="!selectedContact">
				<option value="home">
					🏠 {{ t('maps', 'Home') }}
				</option>
				<option value="work">
					🏢 {{ t('maps', 'Work') }}
				</option>
			</select>
		</div>
		<button class="submit-place-contact"
			:disabled="!selectedContact"
			:class="{ loading: searchingEditedAddress }"
			@click.stop="onValidate">
			<span class="icon-add" />
			{{ t('maps', 'Add address to contact') }}
		</button>
	</div>
</template>

<script>
import accountIconUrl from '@mdi/svg/svg/account.svg'
import L from 'leaflet'
import { generateUrl } from '@nextcloud/router'
import { getCurrentUser } from '@nextcloud/auth'

import { NcSelect, NcAvatar } from '@nextcloud/vue'

import { formatAddress } from '../../utils.js'
import { searchContacts, geocode, searchAddress } from '../../network.js'

export default {
	name: 'PlaceContactPopup',
	
	components: {
		NcSelect,
		Avatar: NcAvatar, // Mapped to match template usage
	},

	props: {
		map: {
			type: Object,
			required: true,
		},
		latLng: {
			type: Object,
			required: true,
		},
	},

	emits: ['contact-placed', 'close'],

	data() {
		return {
			contactData: [],
			contactsLoaded: false,
			contactsLoading: false,
			addressLoading: false,
			addressEdited: false,
			searchingEditedAddress: false,
			addressType: 'home',
			address: null,
			formattedAddress: '',
			selectedContact: null,
		}
	},

	computed: {
		markerIconUrl() {
			return this.selectedContact
				? this.getContactAvatar(this.selectedContact)
					? this.getContactAvatar(this.selectedContact)
					: generateUrl('/apps/maps/contacts-avatar?name=' + encodeURIComponent(this.selectedContact.FN))
				: accountIconUrl
		},
		markerIcon() {
			return L.icon({
				className: 'placement-marker-icon',
				iconSize: [40, 40],
				iconUrl: this.markerIconUrl,
			})
		}
	},

	watch: {
		latLng() {
			this.address = null
			this.formattedAddress = ''
			this.addressEdited = false
			this.getAddress()
			
			// Update marker position natively
			if (this.marker) {
				this.marker.setLatLng(this.latLng)
			}
		},
		markerIconUrl() {
			// Update marker icon natively when the user selects a different contact
			if (this.marker) {
				this.marker.setIcon(this.markerIcon)
			}
		}
	},

	created() {
		// Store leaflet object non-reactively
		this.marker = null
	},

	beforeMount() {
		this.getAddress()
	},

	mounted() {
		// 1. Create native Leaflet marker
		this.marker = L.marker(this.latLng, { icon: this.markerIcon }).addTo(this.map)
		
		// 2. Bind THIS component's HTML to the popup natively
		this.marker.bindPopup(this.$el, { closeButton: true })
		this.marker.on('popupclose', this.onPopupClose)
		
		this.marker.openPopup()
	},

	beforeUnmount() {
		// 3. Clean up native marker on destroy
		if (this.marker && this.map) {
			this.marker.off('popupclose', this.onPopupClose)
			this.map.removeLayer(this.marker)
		}
	},

	methods: {
		getContactKey(contact) {
			return contact.URI
		},
		async loadContacts() {
			if (this.contactsLoaded || this.contactsLoading) {
				return
			}
			this.contactsLoading = true
			try {
				const response = await searchContacts()
				this.contactData = response.data.filter((c) => { return !c.READONLY }).map((c) => {
					return {
						...c,
						AVATAR_URL: this.getContactAvatar(c),
					}
				})
				this.contactsLoaded = true
			} catch (error) {
				console.error(error)
			} finally {
				this.contactsLoading = false
			}
		},
		onPopupClose() {
			this.$emit('close')
		},
		getContactAvatar(contact) {
			if (contact.HAS_PHOTO && contact.HAS_PHOTO2) {
				return generateUrl(
					'/remote.php/dav/addressbooks/users/' + getCurrentUser().uid
					+ '/' + encodeURIComponent(contact.BOOKURI)
					+ '/' + encodeURIComponent(contact.URI) + '?photo').replace(/index\.php\//, '')
			}
			return undefined
		},
		getAddress() {
			this.addressLoading = true
			geocode(this.latLng.lat, this.latLng.lng).then((response) => {
				this.address = response.data.address
				this.formattedAddress = formatAddress(this.address)
			}).catch((error) => {
				console.error(error)
			}).then(() => {
				this.addressLoading = false
			})
		},
		onValidate() {
			if (!this.addressEdited) {
				this.$emit('contact-placed', {
					contact: this.selectedContact,
					latLng: this.latLng,
					address: this.address,
					addressType: this.addressType,
				})
				if (this.map) this.map.closePopup()
			} else {
				this.searchingEditedAddress = true
				searchAddress(this.formattedAddress, 1).then((response) => {
					const res = response.data
					const addressFound = (res.length > 0 && res[0].address && res[0].lat && res[0].lon)
					const address = addressFound ? res[0].address : this.address
					const lat = addressFound ? res[0].lat : this.latLng.lat
					const lng = addressFound ? res[0].lon : this.latLng.lng

					this.$emit('contact-placed', {
						contact: this.selectedContact,
						latLng: { lat, lng },
						address,
						addressType: this.addressType,
					})
					
					if (this.map) this.map.closePopup()
				}).catch((error) => {
					console.error(error)
				}).then(() => {
					this.searchingEditedAddress = false
				})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.submit-place-contact {
	height: 40px !important;
	width: 100%;
}

.contact-select,
.address-type {
	display: flex;
	label {
		margin: auto 0 auto 0;
		text-align: right;
	}
}

.contact-select {
	::v-deep .multiselect {
		flex-grow: 1;
	}
}

.address-type {
	select {
		flex-grow: 1;
		margin: 3px 0 0 0;
	}
}

span.icon {
	display: inline-block;
	height: 34px;
	width: 34px;

	&.icon-loading-small {
		display: block;
		margin-left: auto;
		margin-right: auto;
	}
}

.contact-avatar {
	margin-right: 10px;
}

#place-popup-title {
	text-align: center;
	font-weight: bold;
}

#placeContactPopupAddress {
	border: none;
	width: 100%;
	height: 75px;
	resize: block;
}
</style>
